import React, { useState, useRef, useEffect, useMemo } from 'react';
import './CustomSelect.scss';

interface Option {
  value: string | number;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  value: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Включить поиск в выпадающем списке (для длинных списков 40+) */
  searchable?: boolean;
  searchPlaceholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Выберите опцию",
  className = "",
  disabled = false,
  searchable = false,
  searchPlaceholder = "Поиск..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownWidth, setDropdownWidth] = useState<string>('auto');
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Находим выбранную опцию
  const selectedOption = options.find(option => option.value === value);

  // Фильтруем опции по поисковому запросу (регистронезависимо)
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const q = searchQuery.trim().toLowerCase();
    return options.filter(opt => String(opt.label).toLowerCase().includes(q));
  }, [options, searchQuery, searchable]);

  // Сброс поиска при закрытии
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    } else if (searchable) {
      // Фокус на поле поиска при открытии
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen, searchable]);

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Обработка нажатия клавиш
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        setIsOpen(false);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        // Можно добавить навигацию по клавишам
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Мемоизированное вычисление ширины элементов
  const calculatedWidth = useMemo(() => {
    if (!options.length) return '120px';

    // Упрощенный расчет на основе длины текста
    let maxLength = 0;
    options.forEach(option => {
      if (option.label.length > maxLength) {
        maxLength = option.label.length;
      }
    });
    
    // Приблизительный расчет: 8px на символ + padding
    const estimatedWidth = Math.max(maxLength * 8 + 40, 120);
    return `${estimatedWidth}px`;
  }, [options]);

  // Установка ширины только если она изменилась
  useEffect(() => {
    if (dropdownWidth !== calculatedWidth) {
      setDropdownWidth(calculatedWidth);
    }
  }, [calculatedWidth, dropdownWidth]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (optionValue: string | number) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    // Не закрывать dropdown при Enter в поле поиска
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
    e.stopPropagation();
  };

  return (
    <div 
      ref={selectRef}
      className={`custom-select ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
    >
      <div 
        className="custom-select__trigger"
        onClick={handleToggle}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <span className="custom-select__value">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className={`custom-select__arrow ${isOpen ? 'open' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path 
              d="m1 6 7 7 7-7" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      
      {isOpen && (
        <div 
          className={`custom-select__dropdown ${searchable ? 'custom-select__dropdown--searchable' : ''}`}
          style={{ width: dropdownWidth }}
          onClick={(e) => e.stopPropagation()}
        >
          {searchable && (
            <div className="custom-select__search" onClick={(e) => e.stopPropagation()}>
              <input
                ref={searchInputRef}
                type="text"
                className="custom-select__search-input"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className="custom-select__options-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`custom-select__option ${option.value === value ? 'selected' : ''}`}
                  onClick={() => handleOptionClick(option.value)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="custom-select__no-results">Ничего не найдено</div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default CustomSelect;
